<template>
  <Modal
    class="ui-base-modal"
    :close-button="closeButton"
    :described="Boolean(description)"
    :dismissible="dismissible"
    :open="open"
    :pending="pending"
    @close="$emit('close')"
    @update:open="$emit('update:open', $event)"
  >
    <header class="ui-modal-header">
      <slot name="icon"><FeaturedIcon v-if="iconName" rounded size="lg" :name="iconName" /> </slot>
      <div>
        <DialogTitle as-child>
          <Text as="h2" size="text-lg" weight="semibold">
            <slot name="header">{{ title }}</slot>
          </Text>
        </DialogTitle>
        <DialogDescription v-if="description" as-child>
          <Text color="color-text-text-tertiary-600" size="text-sm">{{ description }}</Text>
        </DialogDescription>
      </div>
    </header>
    <div v-if="hasDivider" aria-hidden="true" class="ui-modal-divider" />
    <div class="ui-modal-content"><slot /></div>
    <div v-if="hasDivider" aria-hidden="true" class="ui-modal-divider" />
    <footer v-if="!hideFooter" class="ui-modal-footer">
      <div class="ui-base-modal__actions">
        <slot name="footer">
          <Button
            color="gray"
            :disabled="cancelDisabled || pending"
            size="lg"
            variant="primary"
            @click="cancel"
          >
            {{ cancelLabel }}
          </Button>
          <Button
            :disabled="submitDisabled"
            :loading="pending"
            size="lg"
            variant="primary"
            @click="$emit('confirm')"
          >
            {{ submitLabel }}
          </Button>
        </slot>
      </div>
    </footer>
  </Modal>
</template>
<script setup lang="ts">
  import { DialogDescription, DialogTitle } from 'reka-ui'

  import { Button } from '../Button'
  import { FeaturedIcon } from '../FeaturedIcon'
  import type { IconName } from '../Icon'
  import { Text } from '../Text'

  import Modal from './Modal.vue'

  const props = withDefaults(
    defineProps<{
      cancelDisabled?: boolean
      cancelLabel?: string
      closeButton?: boolean
      description?: string
      dismissible?: boolean
      hasDivider?: boolean
      iconName?: IconName
      open: boolean
      pending?: boolean
      submitDisabled?: boolean
      submitLabel?: string
      title: string
      hideFooter?: boolean
    }>(),
    {
      cancelLabel: 'Anuluj',
      closeButton: true,
      dismissible: true,
      hasDivider: true,
      submitLabel: 'Zapisz',
      hideFooter: false,
    },
  )
  const emit = defineEmits<{
    cancel: []
    close: []
    confirm: []
    'update:open': [value: boolean]
  }>()
  const cancel = () => {
    if (props.pending || props.cancelDisabled) return
    emit('cancel')
    emit('update:open', false)
    emit('close')
  }
</script>
<style scoped>
  .ui-base-modal__actions {
    display: flex;
    flex-direction: row;
    gap: 12px;
    align-items: center;
    justify-content: flex-end;
  }
</style>
